import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  X,
  Save,
  ImageIcon,
  Star,
  Package as PackageIcon,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { formatPrice } from '../lib/format';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  slug: '',
  price: '',
  old_price: '',
  discount: 0,
  category: 'genti',
  in_stock: true,
  image: '',
  description: '',
  sku: '',
  featured: false,
};

const ProductForm = ({ open, product, categories, onClose, onSaved }) => {
  const { authHeaders } = useAdminAuth();
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (product) {
      setForm({
        name: product.name || '',
        slug: product.slug || '',
        price: product.price ?? '',
        old_price: product.old_price ?? '',
        discount: product.discount ?? 0,
        category: product.category || 'genti',
        in_stock: product.in_stock ?? true,
        image: product.image || '',
        description: product.description || '',
        sku: product.sku || '',
        featured: product.featured ?? false,
      });
    } else {
      setForm(emptyForm);
    }
    setError(null);
  }, [product, open]);

  if (!open) return null;

  const change = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [field]: val });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const cat = categories.find((c) => c.slug === form.category);
      const payload = {
        name: form.name,
        slug: form.slug || form.name,
        price: parseFloat(form.price) || 0,
        old_price: form.old_price ? parseFloat(form.old_price) : null,
        discount: parseInt(form.discount) || 0,
        category: form.category,
        category_name: cat?.name || form.category,
        in_stock: !!form.in_stock,
        image: form.image,
        images: [form.image].filter(Boolean),
        description: form.description,
        sku: form.sku,
        featured: !!form.featured,
      };
      if (product) {
        await axios.patch(`${API}/admin/products/${product.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeaders() });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="ml-auto relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-serif font-bold text-xl">
            {product ? 'Editare produs' : 'Produs nou'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 flex items-start gap-2 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Image preview */}
          {form.image && (
            <div className="relative">
              <img
                src={form.image}
                alt="Preview"
                className="w-full h-56 object-cover rounded-xl bg-neutral-50 border border-neutral-200"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">URL Imagine *</label>
            <div className="relative">
              <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                required
                type="url"
                value={form.image}
                onChange={change('image')}
                placeholder="https://..."
                className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
              />
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">
              Lipiți URL-ul direct al imaginii (ex. de pe Unsplash, axiomas.shop sau orice host)
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Denumire produs *</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={change('name')}
              className="w-full px-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Slug URL (opțional, se generează automat dacă e gol)
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={change('slug')}
              placeholder="ex: geanta-business-piele"
              className="w-full px-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm font-mono"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Preț (lei) *</label>
              <input
                required
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={change('price')}
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Preț vechi (lei)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.old_price}
                onChange={change('old_price')}
                placeholder="—"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Reducere %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.discount}
                onChange={change('discount')}
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Categorie *</label>
              <select
                required
                value={form.category}
                onChange={change('category')}
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm bg-white cursor-pointer"
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">SKU (cod produs)</label>
              <input
                type="text"
                value={form.sku}
                onChange={change('sku')}
                placeholder="AX-XXXXX"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Descriere</label>
            <textarea
              rows="4"
              value={form.description}
              onChange={change('description')}
              className="w-full px-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
            />
          </div>

          <div className="flex flex-wrap gap-5 pt-2 border-t border-neutral-200">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.in_stock}
                onChange={change('in_stock')}
                className="accent-amber-500 w-4 h-4"
              />
              <span className="text-sm text-neutral-700">Disponibil în stoc</span>
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={change('featured')}
                className="accent-amber-500 w-4 h-4"
              />
              <span className="text-sm text-neutral-700">Produs evidențiat</span>
            </label>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-neutral-200">
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {product ? 'Salvează modificările' : 'Adaugă produs'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-neutral-700 hover:text-neutral-900 font-medium"
            >
              Anulează
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
};

const AdminProducts = () => {
  const { authHeaders } = useAdminAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/products`, { headers: authHeaders() }),
      axios.get(`${API}/categories`),
    ])
      .then(([p, c]) => {
        setProducts(p.data || []);
        setCategories(c.data || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = products.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.slug?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q)
    );
  });

  const confirmDelete = async () => {
    if (!deleteId) return;
    await axios.delete(`${API}/admin/products/${deleteId}`, { headers: authHeaders() });
    setDeleteId(null);
    load();
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-4 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Caută după nume, slug sau SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 focus:border-amber-500 outline-none rounded-lg text-sm"
          />
        </div>
        <button
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Produs nou
        </button>
      </div>

      {/* Products grid */}
      <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Se încarcă...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">
            <PackageIcon className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
            Nu există produse.
          </div>
        ) : (
          <div className="divide-y divide-neutral-100">
            {filtered.map((p) => (
              <div key={p.id} className="p-4 flex items-center gap-4 hover:bg-amber-50/30 transition-colors">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-16 h-16 object-cover rounded-lg bg-neutral-50 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-neutral-900 truncate">{p.name}</h3>
                    {p.featured && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-medium">
                        <Star className="w-3 h-3 fill-current" /> Featured
                      </span>
                    )}
                    {!p.in_stock && (
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-medium">
                        Indisponibil
                      </span>
                    )}
                    {p.discount > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-neutral-900 text-white text-[10px] font-bold">
                        -{p.discount}%
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap gap-3">
                    <span className="font-mono">{p.sku}</span>
                    <span>·</span>
                    <span>{p.category_name}</span>
                    <span>·</span>
                    <span className="font-mono truncate">/{p.slug}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-serif font-bold text-lg text-neutral-900">{formatPrice(p.price)}</div>
                  {p.old_price && (
                    <div className="text-xs text-neutral-400 line-through">{formatPrice(p.old_price)}</div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => { setEditing(p); setFormOpen(true); }}
                    className="p-2 hover:bg-amber-100 rounded-lg text-amber-700 transition-colors"
                    title="Editează"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteId(p.id)}
                    className="p-2 hover:bg-red-100 rounded-lg text-red-600 transition-colors"
                    title="Șterge"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ProductForm
        open={formOpen}
        product={editing}
        categories={categories}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={load}
      />

      {/* Delete confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteId(null)} />
          <div className="relative bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-lg text-neutral-900">Ștergi produsul?</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Această acțiune este permanentă. Produsul va dispărea din magazin.
                </p>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="px-5 py-2.5 text-neutral-700 hover:text-neutral-900 font-medium text-sm"
              >
                Anulează
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold text-sm"
              >
                Da, șterge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
